<script setup lang="ts">
/**
 * NeedHelpDialog — Login-page help popup
 *
 * Shown when the user clicks "Need Help?" on the login form.
 * - Shows any non-null contact detail fields (facility name, contact person,
 *   contact phone, contact email) configured in tenant settings.
 * - If all four are absent, falls back to a generic "contact your administrator" message.
 */
import { useTranslation } from 'i18next-vue';
import { DialogModal, Icon } from '@tms-phoenix/ui-vue';

const props = defineProps<{
  visible: boolean;
  facilityName?: string | null;
  contactPerson?: string | null;
  contactPhone?: string | null;
  contactEmail?: string | null;
  /** @deprecated use contactEmail instead */
  supportEmail?: string | null;
}>();

const emit = defineEmits<{
  'update:visible': [value: boolean];
}>();

const { t } = useTranslation();

const resolvedEmail = computed(() => props.contactEmail ?? props.supportEmail ?? null);

const hasAnyContact = computed(() =>
  !!(props.facilityName || props.contactPerson || props.contactPhone || resolvedEmail.value),
);
</script>

<template>
  <DialogModal
    :visible="props.visible"
    :title="t('login.needHelp')"
    max-width="max-w-sm"
    :show-footer="false"
    @update:visible="emit('update:visible', $event)"
  >
    <template v-if="hasAnyContact">
      <p class="mb-4 text-sm text-gray-500">
        {{ t('login.needHelpWithContact') }}
      </p>

      <div class="flex flex-col gap-3">
        <div
          v-if="props.facilityName"
          class="flex items-center gap-3 rounded-lg bg-blue-50 px-4 py-2.5"
        >
          <Icon
            name="Hospital"
            size-class="h-4 w-4 shrink-0"
            color="text-blue-600"
            variant="stroke"
          />
          <div>
            <p class="mb-0.5 text-xs font-medium uppercase leading-none tracking-wide text-blue-500">
              {{ t('login.needHelpFacilityName') }}
            </p>
            <p class="text-sm font-medium text-blue-800">
              {{ props.facilityName }}
            </p>
          </div>
        </div>

        <div
          v-if="props.contactPerson"
          class="flex items-center gap-3 rounded-lg bg-blue-50 px-4 py-2.5"
        >
          <Icon
            name="PersonOutline"
            size-class="h-4 w-4 shrink-0"
            color="text-blue-600"
            variant="fill"
          />
          <div>
            <p class="mb-0.5 text-xs font-medium uppercase leading-none tracking-wide text-blue-500">
              {{ t('login.needHelpContactPerson') }}
            </p>
            <p class="text-sm font-medium text-blue-800">
              {{ props.contactPerson }}
            </p>
          </div>
        </div>

        <div
          v-if="props.contactPhone"
          class="flex items-center gap-3 rounded-lg bg-blue-50 px-4 py-2.5"
        >
          <Icon
            name="Phone"
            size-class="h-4 w-4 shrink-0"
            color="text-blue-600"
            variant="fill"
          />
          <div>
            <p class="mb-0.5 text-xs font-medium uppercase leading-none tracking-wide text-blue-500">
              {{ t('common.phone') }}
            </p>
            <p class="text-sm font-medium text-blue-800">
              {{ props.contactPhone }}
            </p>
          </div>
        </div>

        <div
          v-if="resolvedEmail"
          class="flex items-center gap-3 rounded-lg bg-blue-50 px-4 py-2.5"
        >
          <Icon
            name="Mail"
            size-class="h-4 w-4 shrink-0"
            color="text-blue-700"
            variant="fill"
          />
          <div>
            <p class="mb-0.5 text-xs font-medium uppercase leading-none tracking-wide text-blue-500">
              {{ t('common.email') }}
            </p>
            <p class="text-sm font-medium break-all text-blue-800">
              {{ resolvedEmail }}
            </p>
          </div>
        </div>
      </div>
    </template>

    <!-- No contact details at all — generic fallback -->
    <template v-else>
      <p class="text-sm leading-relaxed text-gray-500">
        {{ t('login.needHelpNoEmail') }}
      </p>
    </template>
  </DialogModal>
</template>

